import type { ChatMention } from "../types/domain";

export type ChatSegment =
  | { type: "text"; value: string }
  | { type: "link"; url: string }
  | { type: "image"; url: string }
  | { type: "mention"; playerId: number; name: string }
  | { type: "route-link"; text: string; path: string };

const URL_REGEX = /https?:\/\/[^\s]+/g;
// Matches `[label](/internal/path)` — internal SPA routes only.
const ROUTE_LINK_REGEX = /\[([^\]]+)\]\((\/[^\s)]+)\)/g;
const IMAGE_EXTENSIONS = [".gif", ".jpg", ".jpeg", ".png", ".webp", ".avif"];
// Trailing characters more likely to be sentence punctuation than part of the URL.
const TRAILING_PUNCT = /[.,;:!?)\]}>'"`]+$/;

const stripTrailingPunctuation = (url: string): { url: string; trail: string } => {
  const match = url.match(TRAILING_PUNCT);
  if (!match) return { url, trail: "" };
  return { url: url.slice(0, -match[0].length), trail: match[0] };
};

const isImageUrl = (url: string): boolean => {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return IMAGE_EXTENSIONS.some(ext => pathname.endsWith(ext));
  } catch {
    return false;
  }
};

const parseMentionsInText = (text: string, mentions: ChatMention[]): ChatSegment[] => {
  if (!mentions.length || !text.includes("@")) return [{ type: "text", value: text }];
  const sorted = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const segments: ChatSegment[] = [];
  let i = 0;
  let buf = "";
  while (i < text.length) {
    if (text[i] === "@") {
      const rest = text.slice(i + 1);
      const hit = sorted.find(m => rest.startsWith(m.name));
      if (hit) {
        if (buf) {
          segments.push({ type: "text", value: buf });
          buf = "";
        }
        segments.push({ type: "mention", playerId: hit.playerId, name: hit.name });
        i += 1 + hit.name.length;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  if (buf) segments.push({ type: "text", value: buf });
  return segments;
};

/** Parse a text chunk (no route links inside) for URLs + mentions. */
const parseTextChunk = (text: string, mentions: ChatMention[]): ChatSegment[] => {
  const segments: ChatSegment[] = [];
  let lastIndex = 0;

  const pushMentionsText = (value: string) => {
    if (!value) return;
    for (const seg of parseMentionsInText(value, mentions)) {
      segments.push(seg);
    }
  };

  for (const match of text.matchAll(URL_REGEX)) {
    const start = match.index ?? 0;
    const raw = match[0];
    const { url, trail } = stripTrailingPunctuation(raw);

    if (start > lastIndex) {
      pushMentionsText(text.slice(lastIndex, start));
    }
    segments.push(isImageUrl(url) ? { type: "image", url } : { type: "link", url });
    if (trail) {
      pushMentionsText(trail);
    }
    lastIndex = start + raw.length;
  }

  if (lastIndex < text.length) {
    pushMentionsText(text.slice(lastIndex));
  }
  return segments;
};

export const parseChatMessage = (content: string, mentions: ChatMention[] = []): ChatSegment[] => {
  const segments: ChatSegment[] = [];
  let lastIndex = 0;

  // Extract markdown route links first (system messages carry these). Text between
  // them is passed through URL + mention parsing.
  for (const match of content.matchAll(ROUTE_LINK_REGEX)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push(...parseTextChunk(content.slice(lastIndex, start), mentions));
    }
    segments.push({ type: "route-link", text: match[1], path: match[2] });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push(...parseTextChunk(content.slice(lastIndex), mentions));
  }
  return segments;
};
